'use client';

import { useMemo, useEffect, useCallback } from 'react';

import { useSetState } from '../../../hooks/use-set-state';

import axios, { endpoints } from '../../../utils/axios';

import { STORAGE_KEY } from './constant';
import { AuthContext } from '../auth-context';
import { setSession, isValidToken } from './utils';

// ----------------------------------------------------------------------

export function AuthProvider({ children }) {
  const { state, setState } = useSetState({
    user: null,
    loading: true,
  });

  useEffect(() => {
    console.log('🔄 JWT AuthProvider: Initializing authentication provider', {
      initialState: {
        hasUser: !!state.user,
        loading: state.loading
      },
      timestamp: new Date().toISOString()
    });
  }, []);

  const checkUserSession = useCallback(async () => {
    try {
      console.log('🔄 JWT AuthProvider: Checking user session');
      
      const accessToken = sessionStorage.getItem(STORAGE_KEY);
      
      console.log('📝 JWT AuthProvider: Session check details', {
        hasToken: !!accessToken,
        tokenValid: accessToken ? isValidToken(accessToken) : false,
        storageKey: STORAGE_KEY,
        timestamp: new Date().toISOString()
      });

      if (accessToken && isValidToken(accessToken)) {
        console.log('✅ JWT AuthProvider: Valid token found, setting session');
        setSession(accessToken);

        console.log('🔄 JWT AuthProvider: Fetching user data from API');
        const res = await axios.get(endpoints.auth.me);

        const { user } = res.data;
        
        console.log('✅ JWT AuthProvider: User data retrieved successfully', {
          userId: user?.id,
          userEmail: user?.email,
          userRole: user?.role,
          hasAccessToken: !!accessToken,
          timestamp: new Date().toISOString()
        });

        setState({ user: { ...user, accessToken }, loading: false });
      } else {
        console.log('❌ JWT AuthProvider: No valid token found, clearing session');
        setState({ user: null, loading: false });
      }
    } catch (error) {
      console.error('❌ JWT AuthProvider: Session check failed', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        endpoint: endpoints.auth.me,
        timestamp: new Date().toISOString()
      });
      setState({ user: null, loading: false });
    }
  }, [setState]);

  useEffect(() => {
    console.log('🔄 JWT AuthProvider: Component mounted, initiating session check');
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => {
      const contextValue = {
        user: state.user
          ? {
            ...state.user,
            role: state.user?.role ?? 'admin',
          }
          : null,
        checkUserSession,
        loading: status === 'loading',
        authenticated: status === 'authenticated',
        unauthenticated: status === 'unauthenticated',
      };
      
      console.log('📊 JWT AuthProvider: Context value updated', {
        hasUser: !!contextValue.user,
        userRole: contextValue.user?.role,
        status,
        loading: contextValue.loading,
        authenticated: contextValue.authenticated,
        timestamp: new Date().toISOString()
      });
      
      return contextValue;
    },
    [checkUserSession, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
